/**
 * Users Page
 * Admin user management with beta tester and admin controls
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Loading, ErrorMessage } from '@/components/common';
import { usersApi } from '@/lib/api/users';
import { useI18n } from '@/i18n';
import type { UserCompleteView, UserCompleteListParams, UserRoleLevel } from '@/types/users';
import { UserResourcesModal } from '@/features/resources';
import { UserSubscriptionModal, UserDetailDrawer } from '@/features/users';
import {
  Users,
  Search,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  UserCheck,
  UserX,
  Shield,
  ShieldOff,
  Crown,
  FolderOpen,
  CreditCard,
  Star,
  User,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  Bell,
  MapPin,
  Smartphone,
  Tablet,
  Monitor,
  Clock,
} from 'lucide-react';

const ROLE_COLORS: Record<UserRoleLevel, string> = {
  free: 'bg-gray-100 text-gray-800',
  vip: 'bg-yellow-100 text-yellow-800',
  premium: 'bg-purple-100 text-purple-800',
  pro: 'bg-blue-100 text-blue-800',
};

// Helper function to get device icon and label
function getDeviceInfo(deviceMeta: Record<string, unknown> | null) {
  if (!deviceMeta) return null;
  const platform = (deviceMeta.platform as string)?.toLowerCase() || '';
  const deviceType = (deviceMeta.deviceType as string)?.toLowerCase() || '';

  if (platform.includes('ios') || platform.includes('iphone') || platform.includes('ipad')) {
    if (deviceType.includes('tablet') || platform.includes('ipad')) {
      return { icon: Tablet, label: 'iPad', color: 'text-gray-600' };
    }
    return { icon: Smartphone, label: 'iOS', color: 'text-gray-600' };
  }
  if (platform.includes('android')) {
    if (deviceType.includes('tablet')) {
      return { icon: Tablet, label: 'Android', color: 'text-green-600' };
    }
    return { icon: Smartphone, label: 'Android', color: 'text-green-600' };
  }
  if (platform.includes('web') || platform.includes('browser')) {
    return { icon: Monitor, label: 'Web', color: 'text-blue-600' };
  }
  return { icon: Monitor, label: platform || 'Unknown', color: 'text-gray-500' };
}

export function UsersPage() {
  const { messages, language } = useI18n();
  const t = messages.users;

  // Helper function to format relative date with i18n
  const formatRelativeDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const rd = t.relativeDates;
    if (diffDays === 0) return rd?.today || 'Today';
    if (diffDays === 1) return rd?.yesterday || 'Yesterday';
    if (diffDays < 7) return (rd?.daysAgo || '{count} days ago').replace('{count}', String(diffDays));
    if (diffDays < 30) return (rd?.weeksAgo || '{count} weeks ago').replace('{count}', String(Math.floor(diffDays / 7)));
    if (diffDays < 365) return (rd?.monthsAgo || '{count} months ago').replace('{count}', String(Math.floor(diffDays / 30)));
    return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  // Helper function to format full date for tooltip
  const formatFullDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const queryClient = useQueryClient();
  const [params, setParams] = useState<UserCompleteListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'updated_at',
    sort_order: 'desc',
  });
  const [search, setSearch] = useState('');
  const [filterBetaTester, setFilterBetaTester] = useState<boolean | undefined>(undefined);
  const [filterRole, setFilterRole] = useState<string | undefined>(undefined);
  const [filterHasAstro, setFilterHasAstro] = useState<boolean | undefined>(undefined);
  const [filterAdmin, setFilterAdmin] = useState<boolean | undefined>(undefined);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [resourcesModalUser, setResourcesModalUser] = useState<{ userId: string; userName?: string } | null>(null);
  const [subscriptionModalUser, setSubscriptionModalUser] = useState<{ userId: string; userName?: string } | null>(null);
  const [detailDrawerUser, setDetailDrawerUser] = useState<UserCompleteView | null>(null);

  // Fetch users
  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-users', params, filterBetaTester, filterRole, filterHasAstro, filterAdmin],
    queryFn: () => usersApi.listUsersComplete({
      ...params,
      is_beta_tester: filterBetaTester,
      role_level: filterRole,
      has_astro_profile: filterHasAstro,
      is_admin: filterAdmin,
    }),
  });

  // Fetch comprehensive user stats
  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: usersApi.getUserStats,
  });

  // Update beta tester status mutation
  const updateBetaTesterMutation = useMutation({
    mutationFn: ({ userId, isBetaTester }: { userId: string; isBetaTester: boolean }) =>
      usersApi.updateBetaTesterStatus(userId, isBetaTester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  // Batch update mutation
  const batchUpdateMutation = useMutation({
    mutationFn: ({ userIds, isBetaTester }: { userIds: string[]; isBetaTester: boolean }) =>
      usersApi.batchUpdateBetaTesters(userIds, isBetaTester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      setSelectedUsers(new Set());
    },
  });

  // Update admin status mutation
  const updateAdminMutation = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      usersApi.updateAdminStatus(userId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  // Update role level mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleLevel }: { userId: string; roleLevel: UserRoleLevel }) =>
      usersApi.updateUserRole(userId, roleLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  const handleSearch = () => {
    setParams((prev) => ({ ...prev, page: 1, search: search || undefined }));
  };

  const handleFilterChange = (filter: 'all' | 'beta' | 'non-beta') => {
    setFilterBetaTester(filter === 'beta' ? true : filter === 'non-beta' ? false : undefined);
    setParams((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const selectAll = () => {
    if (usersData?.users) {
      setSelectedUsers(new Set(usersData.users.map((u) => u.user_id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const handleBatchEnable = () => {
    if (selectedUsers.size > 0) {
      batchUpdateMutation.mutate({ userIds: Array.from(selectedUsers), isBetaTester: true });
    }
  };

  const handleBatchDisable = () => {
    if (selectedUsers.size > 0) {
      batchUpdateMutation.mutate({ userIds: Array.from(selectedUsers), isBetaTester: false });
    }
  };

  // Sort handling
  const handleSort = (column: UserCompleteListParams['sort_by']) => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      sort_by: column,
      sort_order: prev.sort_by === column && prev.sort_order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ column }: { column: UserCompleteListParams['sort_by'] }) => {
    if (params.sort_by !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    }
    return params.sort_order === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
            <p className="text-sm text-gray-500">{t.pageDescription}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t.refresh}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.totalUsers}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
              </div>
              <Users className="w-10 h-10 text-blue-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.admins}</p>
                <p className="text-2xl font-bold text-red-600">{stats.total_admins}</p>
              </div>
              <Shield className="w-10 h-10 text-red-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.betaTesters}</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_beta_testers}</p>
              </div>
              <FlaskConical className="w-10 h-10 text-purple-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.usersWithAstro || '有星盘用户'}</p>
                <p className="text-2xl font-bold text-amber-600">{stats.users_with_astro_profile}</p>
              </div>
              <Star className="w-10 h-10 text-amber-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.pushEnabled || '推送已开启'}</p>
                <p className="text-2xl font-bold text-blue-600">{stats.push_enabled_count}</p>
              </div>
              <Bell className="w-10 h-10 text-blue-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.recentSignups || '近7天注册'}</p>
                <p className="text-2xl font-bold text-green-600">{stats.recent_signups_7d}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.roleDistribution}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(stats.role_distribution || {}).map(([role, count]) => (
                    <span
                      key={role}
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        ROLE_COLORS[role as UserRoleLevel] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {t.roles[role as keyof typeof t.roles] || role}: {count}
                    </span>
                  ))}
                </div>
              </div>
              <Crown className="w-10 h-10 text-yellow-100" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card className="bg-white">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <Input
              placeholder={t.filters.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <Button
              variant={filterBetaTester === undefined ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('all')}
            >
              {t.filters.allUsers}
            </Button>
            <Button
              variant={filterBetaTester === true ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('beta')}
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              {t.filters.betaTesters}
            </Button>
            <Button
              variant={filterBetaTester === false ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('non-beta')}
            >
              {t.filters.nonBeta}
            </Button>
          </div>
        </div>

        {/* Advanced Filters Row */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          {/* Role Level Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t.advancedFilters?.roleLevel || '角色'}:</span>
            <select
              value={filterRole || ''}
              onChange={(e) => {
                setFilterRole(e.target.value || undefined);
                setParams((prev) => ({ ...prev, page: 1 }));
              }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t.advancedFilters?.allRoles || '全部'}</option>
              <option value="free">{t.roles.free}</option>
              <option value="vip">{t.roles.vip}</option>
              <option value="premium">{t.roles.premium}</option>
              <option value="pro">{t.roles.pro}</option>
            </select>
          </div>

          {/* Has Astro Profile Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t.advancedFilters?.hasAstroProfile || '星盘'}:</span>
            <div className="flex gap-1">
              <Button
                variant={filterHasAstro === undefined ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterHasAstro(undefined);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                {t.advancedFilters?.all || '全部'}
              </Button>
              <Button
                variant={filterHasAstro === true ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterHasAstro(true);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                <Star className="w-3 h-3 mr-1" />
                {t.advancedFilters?.yes || '有'}
              </Button>
              <Button
                variant={filterHasAstro === false ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterHasAstro(false);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                {t.advancedFilters?.no || '无'}
              </Button>
            </div>
          </div>

          {/* Is Admin Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t.advancedFilters?.isAdmin || '管理员'}:</span>
            <div className="flex gap-1">
              <Button
                variant={filterAdmin === undefined ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterAdmin(undefined);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                {t.advancedFilters?.all || '全部'}
              </Button>
              <Button
                variant={filterAdmin === true ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterAdmin(true);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                <Shield className="w-3 h-3 mr-1" />
                {t.advancedFilters?.yes || '是'}
              </Button>
              <Button
                variant={filterAdmin === false ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilterAdmin(false);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-2 py-1 text-xs"
              >
                {t.advancedFilters?.no || '否'}
              </Button>
            </div>
          </div>

          {/* Clear All Filters */}
          {(filterRole || filterHasAstro !== undefined || filterAdmin !== undefined || filterBetaTester !== undefined) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterRole(undefined);
                setFilterHasAstro(undefined);
                setFilterAdmin(undefined);
                setFilterBetaTester(undefined);
                setParams((prev) => ({ ...prev, page: 1 }));
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              {t.filters.clearFilters || '清除筛选'}
            </Button>
          )}
        </div>

        {/* Batch Actions */}
        {selectedUsers.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {t.batch.selected.replace('{count}', String(selectedUsers.size))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBatchEnable}
              disabled={batchUpdateMutation.isPending}
            >
              <UserCheck className="w-4 h-4 mr-1" />
              {t.batch.enableBeta}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBatchDisable}
              disabled={batchUpdateMutation.isPending}
            >
              <UserX className="w-4 h-4 mr-1" />
              {t.batch.disableBeta}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t.batch.clearSelection}
            </Button>
          </div>
        )}
      </Card>

      {/* Users Table */}
      <Card className="bg-white overflow-hidden">
        {isLoading && <Loading message={t.loading} />}

        {isError && (
          <ErrorMessage error={error instanceof Error ? error.message : t.error} />
        )}

        {usersData && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          usersData.users.length > 0 &&
                          usersData.users.every((u) => selectedUsers.has(u.user_id))
                        }
                        onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="w-48 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.user}
                    </th>
                    <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.realName}
                    </th>
                    <th className="w-40 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.contact}
                    </th>
                    <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.region}
                    </th>
                    <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t.table.timezone}
                      </span>
                    </th>
                    <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.subscription}
                    </th>
                    <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.flags}
                    </th>
                    <th
                      className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      <span className="inline-flex items-center">
                        {t.table.created}
                        <SortIcon column="created_at" />
                      </span>
                    </th>
                    <th
                      className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('updated_at')}
                    >
                      <span className="inline-flex items-center">
                        {t.table.active}
                        <SortIcon column="updated_at" />
                      </span>
                    </th>
                    <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.actions}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersData.users.map((user: UserCompleteView) => (
                    <tr
                      key={user.user_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setDetailDrawerUser(user)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.user_id)}
                          onChange={() => toggleUserSelection(user.user_id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Avatar */}
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt={user.display_name}
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{user.display_name}</p>
                            <p className="text-xs text-gray-500 truncate">@{user.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {user.real_name || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        <div className="min-w-0 space-y-0.5">
                          {user.email && <div className="truncate">{user.email}</div>}
                          {user.phone_number && <div className="truncate">{user.phone_number}</div>}
                          {!user.email && !user.phone_number && <span className="text-gray-300">-</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {user.pref_region_code || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {user.pref_timezone || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2">
                        {/* Subscription/Role Badge - Click to manage */}
                        <button
                          onClick={() => setSubscriptionModalUser({ userId: user.user_id, userName: user.display_name })}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-300 transition-all ${
                            ROLE_COLORS[user.role_level as UserRoleLevel] || 'bg-gray-100 text-gray-800'
                          }`}
                          title={t.actions.manageSubscription}
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                          {t.roles[user.role_level as keyof typeof t.roles] || user.role_level}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {/* Admin Badge */}
                          {user.is_admin && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                              <Shield className="w-3 h-3 mr-0.5" />
                              {t.badges.admin}
                            </span>
                          )}
                          {/* Beta Badge */}
                          {user.is_beta_tester && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              <FlaskConical className="w-3 h-3 mr-0.5" />
                              Beta
                            </span>
                          )}
                          {/* Birth Chart Badge */}
                          {user.has_birth_chart && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              <Star className="w-3 h-3 mr-0.5" />
                              {t.badges.birthChart}
                            </span>
                          )}
                          {/* Device Badge */}
                          {(() => {
                            const deviceInfo = getDeviceInfo(user.pref_device_meta);
                            if (!deviceInfo) return null;
                            const DeviceIcon = deviceInfo.icon;
                            return (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                                title={deviceInfo.label}
                              >
                                <DeviceIcon className={`w-3 h-3 ${deviceInfo.color}`} />
                              </span>
                            );
                          })()}
                          {/* GPS Indicator */}
                          {user.pref_gps_location && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700"
                              title={t.badges.gpsEnabled}
                            >
                              <MapPin className="w-3 h-3" />
                            </span>
                          )}
                          {/* Show dash if no flags */}
                          {!user.is_admin && !user.is_beta_tester && !user.has_birth_chart && !getDeviceInfo(user.pref_device_meta) && !user.pref_gps_location && (
                            <span className="text-gray-300 text-xs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        <span title={formatFullDate(user.created_at)}>
                          {formatRelativeDate(user.created_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        <span title={formatFullDate(user.updated_at)}>
                          {formatRelativeDate(user.updated_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {/* Resources Button - Icon only */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setResourcesModalUser({ userId: user.user_id, userName: user.display_name });
                            }}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                            title={t.actions.viewResources}
                          >
                            <FolderOpen className="w-4 h-4" />
                          </button>
                          {/* Beta Toggle - Icon only */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateBetaTesterMutation.mutate({
                                userId: user.user_id,
                                isBetaTester: !user.is_beta_tester,
                              });
                            }}
                            disabled={updateBetaTesterMutation.isPending}
                            className={`p-1.5 rounded transition-colors ${
                              user.is_beta_tester
                                ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                : 'hover:bg-gray-100 text-gray-400 hover:text-purple-600'
                            }`}
                            title={user.is_beta_tester ? t.actions.removeBeta : t.actions.makeBeta}
                          >
                            <FlaskConical className="w-4 h-4" />
                          </button>
                          {/* Admin Toggle - Icon only */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateAdminMutation.mutate({
                                userId: user.user_id,
                                isAdmin: !user.is_admin,
                              });
                            }}
                            disabled={updateAdminMutation.isPending}
                            className={`p-1.5 rounded transition-colors ${
                              user.is_admin
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'hover:bg-gray-100 text-gray-400 hover:text-red-600'
                            }`}
                            title={user.is_admin ? t.actions.revokeAdmin : t.actions.makeAdmin}
                          >
                            {user.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t.pagination.showing
                  .replace('{start}', String((params.page! - 1) * params.page_size! + 1))
                  .replace('{end}', String(Math.min(params.page! * params.page_size!, usersData.total)))
                  .replace('{total}', String(usersData.total))}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(params.page! - 1)}
                  disabled={params.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  {t.pagination.page
                    .replace('{current}', String(params.page))
                    .replace('{total}', String(Math.ceil(usersData.total / params.page_size!)))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(params.page! + 1)}
                  disabled={!usersData.has_more}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* User Resources Modal */}
      <UserResourcesModal
        isOpen={!!resourcesModalUser}
        userId={resourcesModalUser?.userId || null}
        userName={resourcesModalUser?.userName}
        onClose={() => setResourcesModalUser(null)}
      />

      {/* User Subscription Modal */}
      <UserSubscriptionModal
        isOpen={!!subscriptionModalUser}
        userId={subscriptionModalUser?.userId || null}
        userName={subscriptionModalUser?.userName}
        onClose={() => setSubscriptionModalUser(null)}
      />

      {/* User Detail Drawer */}
      <UserDetailDrawer
        isOpen={!!detailDrawerUser}
        user={detailDrawerUser}
        onClose={() => setDetailDrawerUser(null)}
        onToggleBeta={(userId, isBeta) => {
          updateBetaTesterMutation.mutate({ userId, isBetaTester: isBeta });
          // Update local state to reflect change immediately
          if (detailDrawerUser && detailDrawerUser.user_id === userId) {
            setDetailDrawerUser({ ...detailDrawerUser, is_beta_tester: isBeta });
          }
        }}
        onToggleAdmin={(userId, isAdmin) => {
          updateAdminMutation.mutate({ userId, isAdmin });
          // Update local state to reflect change immediately
          if (detailDrawerUser && detailDrawerUser.user_id === userId) {
            setDetailDrawerUser({ ...detailDrawerUser, is_admin: isAdmin });
          }
        }}
        onUpdateRole={(userId, roleLevel) => {
          updateRoleMutation.mutate({ userId, roleLevel });
          // Update local state to reflect change immediately
          if (detailDrawerUser && detailDrawerUser.user_id === userId) {
            setDetailDrawerUser({ ...detailDrawerUser, role_level: roleLevel });
          }
        }}
        onOpenResources={(userId, userName) => {
          setResourcesModalUser({ userId, userName });
        }}
        onOpenSubscription={(userId, userName) => {
          setSubscriptionModalUser({ userId, userName });
        }}
      />
    </div>
  );
}
